import React, {
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';
import {
  QueryProvider,
  QueryContext,
  useDataModel,
  extend
} from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { Carousel } from '@one-thd/sui-carousel';
import proptypes from 'prop-types';
const { bool: boolType, node: nodeType, shape: shapeType, string: stringType, } = proptypes;
import { IntegratedBrandBathPod } from '../IntegratedBrandBathPod';
import { VisualTabs } from './VisualTabs';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { dynamicRecsDataModel } from '../recs-data-model';
import { requestKey, schemaName } from '../IbtData';

const Wrapper = ({ children }) => {
  const isSSR = typeof window === 'undefined';
  if (isSSR) {
    return (
      <div style={{ maxWidth: '336px' }}>
        {children}
      </div>
    );
  }
  return children;
};
Wrapper.propTypes = {
  children: nodeType.isRequired
};

export const MakeItMatchCollection = ({
  category,
  anchorId,
  storeId
}) => {
  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);
  const { storeZip: zipCode, membershipInformation } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext);
  const isMobile = channel === 'mobile';
  const getAppId = () => {
    let appId = '';
    if (isConsumerApp) {
      appId = 'consumerApp';
    } else if (isMobile) {
      appId = 'mobileweb';
    } else {
      appId = 'desktop';
    }
    return appId;
  };

  const opts = {
    variables: {
      anchorId,
      storeId,
      key: requestKey,
      zipCode,
      apiName: schemaName,
      maxResults: '18',
      appId: getAppId(),
    },
    skip: !anchorId,
    ssr: true
  };

  const {
    data: bathRecsData, loading: bathRecsLoading, error: bathRecsError
  } = useDataModel('mimrecs', opts);

  const { products, metadata, categories } = bathRecsData?.recs || [];

  const getFilterCategoryProducts = () => {
    const filteredCategories = categories?.filter((cat) => {
      if (category !== 'Kitchen') {
        return cat?.category.toLowerCase().indexOf('kitchen') === -1;
      }
      return cat?.category.toLowerCase().indexOf('kitchen') > -1;
    });

    const filteredCategoryProducts = products?.filter((product) => {
      return filteredCategories.some((cat) => cat.itemIds.includes(product.product.itemId));
    });
    return [filteredCategories, filteredCategoryProducts];
  };

  const [filteredCategories, filteredCategoryProducts] = getFilterCategoryProducts();
  const collectionTitle2 = metadata?.collectionTitle2;

  const handleProductClick = useCallback((evt, product) => {
    let productCanonicalUrl = product?.identifiers?.canonicalUrl;
    if (productCanonicalUrl && !(evt.ctrlKey || evt.metaKey)) {
      evt.preventDefault();
      window.open(
        `${window.location.origin}${productCanonicalUrl}`,
        isMobile ? '_self' : '_blank'
      );
    }
  }, []);
  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(() => filteredProducts);
  };

  const MIMdefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
      && { isBrandPricingPolicyCompliant:
        defaultVariables.current.isBrandPricingPolicyCompliant })
  };

  let breakpoints = ({
    sm: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    md: {
      slidesPerView: 4,
      slidesPerGroup: 4
    },
    lg: {
      slidesPerView: 5,
      slidesPerGroup: 5
    },
    xl: {
      slidesPerView: 5,
      slidesPerGroup: 5
    }
  });
  let displayProducts = [];
  if (!bathRecsLoading) {
    if (isCategorySelected) {
      displayProducts = filtered;
    } else {
      displayProducts = filteredCategoryProducts;
    }
  }
  return (
    <>
      {(bathRecsLoading) && (
        <LoadingPlaceholder txtPlaceholderLines={9} />
      )}
      {displayProducts?.length > 0 && (
        <>
          <div className="sui-grid sui-grid-flow-row-dense sui-grid-cols-12 ">
            <div className="sui-col-span-10 sui-pb-4">
              <Typography variant="h2">
                {`${collectionTitle2} for ${category}`}
              </Typography>
            </div>
          </div>
          {filteredCategories?.length > 0 && (
            <VisualTabs
              products={filteredCategoryProducts}
              categories={filteredCategories}
              filterItemIds={pullFilteredData}
            />
          )}
          {displayProducts.length > 0 && (
            <QueryProvider
              dataSource="dynamicRecs"
              cacheKey={`${schemaName}-${category}-recommendations-carousel`}
              defaultVariables={MIMdefaultVariables}
            >
              <Carousel
                breakpoints={breakpoints}
                disableMargin
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isMobile ? ({ slidesPerGroup: 2 }) : {})}
              >
                {displayProducts?.map((dynamicProduct, productIndex) => {
                  const { product } = dynamicProduct;
                  const itemId = product?.identifiers?.itemId;
                  return (
                    <Wrapper key={`integrated-brand-pod-${itemId}-${category}`}>
                      <IntegratedBrandBathPod
                        itemId={itemId}
                        storeId={storeId}
                        position={productIndex + 1}
                        onClick={handleProductClick}
                        parent="IBT Bath Collection"
                      />
                    </Wrapper>
                  );
                })}
              </Carousel>
            </QueryProvider>
          )}
        </>
      )}
    </>
  );
};

MakeItMatchCollection.propTypes = {
  category: stringType,
  anchorId: stringType,
  storeId: stringType.isRequired
};

MakeItMatchCollection.defaultProps = {
  anchorId: '',
  category: 'Bath'
};

MakeItMatchCollection.dataModel = extend({},
  dynamicRecsDataModel
);